import React, { useEffect, useState } from 'react';
import {
  getCurrentEnvironment,
  validateFormDataSessionKey,
  validateItemIdBSLKey,
  validateResponseDataSessionKey,
} from '../collection/Util';
import { Field, Formik, Form, ErrorMessage } from 'formik';
import FormFieldWithLabel from '../../../common/FormFieldWithLabel';
import CustomSelect from '../../../../common/formElements/CustomSelect';
import CustomLoader from '../../../common/CustomLoader';
import EnvironmentSelect from '../../../../common/formElements/EnvironmentSelect';
import { getUserRoleFromApi } from '../../../../common/utils/item-util';
import { pluginCode } from 'drapcode-constant';
import { getItemIdFromOptions } from '../../../../common/utils/event_constants';
import BrowserStorageLocationField from '../BrowserStorageLocationField';
import axios from 'axios';
import { builderSurfaceJsonContentHeader } from '../../../../common/utils/restApiHeaderUtils';
import BrowserStorageOptions from '../BrowserStorageOptions';
import ConsoleLogOptions from '../../ConsoleLogOptions';

export const GenerateOTPForm = ({
  hideModal,
  actions,
  eventAction,
  actionData,
  selectedEvent,
  showLoaderForAction,
  projectId,
  events,
  version,
}) => {
  const isSMS = eventAction.name === 'generateSmsOTP';
  const servicePluginField = isSMS ? 'smsServicePlugin' : 'emailServicePlugin';
  const templateField = isSMS ? 'smsTemplate' : 'emailTemplate';
  const servicePluginLabel = isSMS ? 'SMS Service Plugin' : 'Email Service Plugin';
  const templateLabel = isSMS ? 'SMS Template' : 'Email Template';
  const servicePluginOptions = isSMS ? getSmsServicePluginOptions() : emailServicePluginOptions();

  const [roleOptions, setRoleOptions] = useState([]);
  const [envRefreshCounter, setEnvRefreshCounter] = useState(0);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [envRoleMapping, setEnvRoleMapping] = useState([]);
  const [showBSLOption, setShowBSLOption] = useState(false);
  const [showCLOption, setShowCLOption] = useState(false);

  useEffect(() => {
    const currentEnvironment = getCurrentEnvironment();
    getUserRoleFromApi(projectId, version).then((res) => {
      const roles = res.map((role) => ({
        value: role.uuid,
        label: role.name,
      }));
      setRoleOptions(roles);
    });
    const roleParam = eventAction.parameters.find((param) => param.name === 'role');
    if (roleParam && Array.isArray(roleParam.value)) {
      setEnvRoleMapping(roleParam.value);
    } else {
      setEnvRoleMapping([]);
    }
  }, [actionData, envRefreshCounter]);

  useEffect(() => {
    const templateType = isSMS ? 'SMS' : 'EMAIL';
    const config = builderSurfaceJsonContentHeader(projectId);
    axios
      .get(
        `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/projects/${projectId}/templates?templateType=${templateType}`,
        config,
      )
      .then((res) => {
        const templates = res.data.map((t) => ({
          value: t.uuid,
          label: t.name,
        }));
        setTemplateOptions(templates);
      });
  }, []);

  const pagesOptions = events.pages.map((item) => ({
    value: '/' + item.slug,
    label: item.name,
  }));

  function getPluginOptions(plugins, providerMap, defaultOption) {
    const installedCodes = new Set(plugins.map((p) => p.code));
    return [
      defaultOption,
      ...providerMap
        .filter(({ code }) => installedCodes.has(code))
        .map(({ value, label }) => ({ value, label })),
    ];
  }

  function emailServicePluginOptions() {
    const providerMap = [
      { code: pluginCode.AWS_SES, value: 'AWS_SES', label: 'AWS SES' },
      { code: pluginCode.SENDGRID, value: 'SENDGRID', label: 'SendGrid' },
      { code: pluginCode.RESEND, value: 'RESEND', label: 'Resend' },
    ];
    return getPluginOptions(events.plugins, providerMap, {
      value: '',
      label: 'DrapCode Default Email Provider',
    });
  }

  function getSmsServicePluginOptions() {
    const providerMap = [
      { code: pluginCode.AWS_SNS, value: 'AWS_SNS', label: 'AWS SNS' },
      { code: pluginCode.TWILIO, value: 'TWILIO', label: 'Twilio' },
      { code: pluginCode.PARKEN, value: 'PARKEN', label: 'Parken' },
      {
        code: pluginCode.AFRICAS_TALKING_SMS,
        value: 'AFRICAS_TALKING_SMS',
        label: "Africa's Talking",
      },
      { code: pluginCode.HOSTPINNACLE, value: 'HOSTPINNACLE', label: 'HostPinnacle' },
      { code: pluginCode.TELNYX, value: 'TELNYX', label: 'Telnyx' },
    ];
    return getPluginOptions(events.plugins, providerMap, { value: '', label: '-Select One-' });
  }

  const createInitialValues = () => {
    let values = {};
    actionData.args.forEach((arg) => {
      const dbParam = eventAction.parameters.find((p) => p.name === arg.name);
      values[arg.name] = dbParam ? dbParam.value : '';
    });
    const currentEnvironment = getCurrentEnvironment();
    const roleParam = eventAction.parameters.find((p) => p.name === 'role');
    let roleValue = '';
    if (roleParam && Array.isArray(roleParam.value)) {
      const envRole = roleParam.value.find((r) => r.environment === currentEnvironment);
      roleValue = envRole ? envRole.value : '';
    }
    return {
      ...values,
      otpAuthenticationType: values.otpAuthenticationType || 'login',
      otpVerificationPage: values.otpVerificationPage || '',
      role: roleValue,
      [servicePluginField]: values[servicePluginField] || '',
      [templateField]: values[templateField] || '',
    };
  };

  const refreshMethod = () => {
    setEnvRefreshCounter((prev) => prev + 1);
  };

  const bslOptionHandler = () => {
    setShowBSLOption(!showBSLOption);
  };

  const clogOptionHandler = () => {
    setShowCLOption(!showCLOption);
  };

  const handleSubmit = (values) => {
    const currentEnvironment = getCurrentEnvironment();
    const updatedRoleMapping = [
      ...envRoleMapping.filter((r) => r.environment !== currentEnvironment),
      {
        value: values.role,
        environment: currentEnvironment,
      },
    ];
    values.role = updatedRoleMapping;
    let parameters = [];
    Object.keys(values).forEach((key) => {
      const action = actionData.args.find((param) => param.name === key);
      parameters.push({
        name: key,
        value: values[key],
        type: action.type || 'dropdown',
      });
    });
    let actionFormData = eventAction;
    actionFormData.parameters = parameters;
    actions.updateAction({
      projectId,
      version,
      eventId: selectedEvent.uuid,
      data: actionFormData,
    });
    hideModal();
  };

  return (
    <Formik enableReinitialize initialValues={createInitialValues()} onSubmit={handleSubmit}>
      {({ values, setFieldValue, setFieldTouched }) => {
        const currentEnvironment = getCurrentEnvironment();
        const roleObj = envRoleMapping.find((r) => r.environment === currentEnvironment);
        const currentRole = roleObj ? roleObj.value : '';
        return (
          <Form>
            <div className="row">
              <div className="col-12">
                <FormFieldWithLabel label="Authentication Type">
                  <CustomSelect
                    name="otpAuthenticationType"
                    options={[
                      { label: 'Login', value: 'login' },
                      { label: 'Sign Up', value: 'signUp' },
                    ]}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    value={values.otpAuthenticationType}
                  />
                </FormFieldWithLabel>
              </div>
              {values.otpAuthenticationType === 'signUp' && (
                <>
                  <div className="col-12 mt-2">
                    <EnvironmentSelect
                      actions={actions}
                      projectId={projectId}
                      version={version}
                      refreshMethod={refreshMethod}
                    />
                  </div>
                  <div className="col-12">
                    <FormFieldWithLabel label="Select Role (Environment Based)">
                      <CustomSelect
                        name="role"
                        options={roleOptions}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        value={values.role || currentRole}
                        isDisabled={values.otpAuthenticationType !== 'signUp'}
                      />
                    </FormFieldWithLabel>
                  </div>
                </>
              )}
              <div className="col-12">
                <FormFieldWithLabel label="OTP Verification Page">
                  <CustomSelect
                    name="otpVerificationPage"
                    options={pagesOptions}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    value={values.otpVerificationPage}
                  />
                </FormFieldWithLabel>
              </div>
              <div className="col-12">
                <FormFieldWithLabel label={servicePluginLabel}>
                  <CustomSelect
                    name={servicePluginField}
                    options={servicePluginOptions}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    value={values[servicePluginField]}
                  />
                </FormFieldWithLabel>
              </div>
              <div className="col-12">
                <FormFieldWithLabel label={templateLabel}>
                  <CustomSelect
                    name={templateField}
                    options={templateOptions}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    value={values[templateField]}
                  />
                </FormFieldWithLabel>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <FormFieldWithLabel cta={false} label="Success Message" icon="text">
                    <Field
                      name="successMessage"
                      type="text"
                      placeholder="(Optional) Enter custom success message."
                      className="form-control form-control-sm"
                    />
                  </FormFieldWithLabel>
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <FormFieldWithLabel cta={false} label="Error Message" icon="text">
                    <Field
                      name="errorMessage"
                      type="text"
                      placeholder="(Optional) Enter custom error message."
                      className="form-control form-control-sm"
                    />
                  </FormFieldWithLabel>
                </div>
              </div>
            </div>
            <div className="row ml-3">
              <div className="col-12 d-flex gap-2">
                <button
                  onClick={bslOptionHandler}
                  className="btn btn-outline-info btn-sm"
                  type="button"
                >
                  <i className="wb webconnect-cog"></i>&nbsp;Browser Storage Options
                </button>
                &nbsp;
                <button
                  onClick={clogOptionHandler}
                  className="btn btn-outline-info btn-sm"
                  type="button"
                >
                  <i className="wb webconnect-cog"></i>&nbsp;Console Log Options
                </button>
              </div>
            </div>
            {showBSLOption && (
              <div className="row mt-2">
                <div className="col-12">
                  <BrowserStorageOptions
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    validateFormDataSessionKey={validateFormDataSessionKey}
                    validateResponseDataSessionKey={validateResponseDataSessionKey}
                  />
                </div>
              </div>
            )}
            {showCLOption && (
              <div className="row mt-2">
                <div className="col-12">
                  <ConsoleLogOptions values={values} />
                </div>
              </div>
            )}
            <div className="row mt-3">
              <div className="col-6">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={hideModal}
                >
                  Cancel
                </button>
              </div>
              <div className="col-6 text-right">
                <button type="submit" className="btn btn-theme-submit btn-sm">
                  Save
                  <CustomLoader loading={showLoaderForAction} />
                </button>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
